import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';
import { PerformanceMetric } from '../../../constants/telemetry';
import { ListProjectEnvironmentTypesResponse } from '../../../data/services/resource-manager/project-environment-type';
import {
    AggregatedSuccess,
    ClientError,
    FailureOperation,
    isAggregatedFailure,
    isAggregatedSuccess,
} from '../../../models/common';
import { combineResults, getErrorCodes } from '../../../utilities/aggregated-result';
import { aggregateFailureResponses } from '../../../utilities/failure';
import { trackTimedPerformanceMetric } from '../../../utilities/telemetry/channel';
import {
    discoverProjectEnvironmentTypes,
    discoverProjectEnvironmentTypesError,
    discoverProjectEnvironmentTypesFailed,
    discoverProjectEnvironmentTypesSuccess,
    listProjectEnvironmentTypes,
} from '../../actions/project-environment-type/project-environment-type-action-creators';
import { DiscoverProjectEnvironmentTypesAction } from '../../actions/project-environment-type/project-environment-type-actions';
import { createSagaError } from '../../effects/create-saga-error';
import { putAndAwait } from '../../effects/put-and-await';
import { rejectAction } from '../../effects/reject-action';
import { resolveAction } from '../../effects/resolve-action';
import { takeLeading } from '../../effects/take';
import { AsyncOutcome } from '../../store/common-state';
import { getResourceIdsForProjectsAuthorizedForProjectEnvironmentTypeRead } from './selectors';

export function* discoverProjectEnvironmentTypesSaga(action: DiscoverProjectEnvironmentTypesAction): SagaIterator {
    const startTime = new Date();

    try {
        const projectResourceIds: string[] = yield select(
            getResourceIdsForProjectsAuthorizedForProjectEnvironmentTypeRead
        );

        // Exit immediately if no resource IDs are available
        if (projectResourceIds.length < 1) {
            yield put(discoverProjectEnvironmentTypesSuccess());
            yield resolveAction(action, AggregatedSuccess());
            yield call(
                trackTimedPerformanceMetric,
                PerformanceMetric.DiscoverProjectEnvironmentTypes,
                startTime,
                AsyncOutcome.Success
            );
            return;
        }

        const responses: ListProjectEnvironmentTypesResponse[] = yield putAndAwait(
            listProjectEnvironmentTypes(projectResourceIds.map((id) => ({ id })))
        );

        const completeResult = combineResults(responses);

        if (isAggregatedFailure(completeResult)) {
            const { failures } = completeResult;
            const failure = aggregateFailureResponses(FailureOperation.DiscoverProjectEnvironmentTypes, ...failures);
            yield put(discoverProjectEnvironmentTypesFailed({ failure }));
        } else {
            yield put(discoverProjectEnvironmentTypesSuccess());
        }

        yield resolveAction(action, completeResult);

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.DiscoverProjectEnvironmentTypes,
            startTime,
            completeResult.outcome,
            isAggregatedSuccess(completeResult) ? undefined : { errorCodes: getErrorCodes(completeResult) }
        );
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.DiscoverProjectEnvironmentTypes);
        yield put(discoverProjectEnvironmentTypesError({ error }));
        yield rejectAction(action, error);

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.DiscoverProjectEnvironmentTypes,
            startTime,
            AsyncOutcome.Error,
            { errorCodes: [error.code] }
        );
    }
}

export function* discoverProjectEnvironmentTypesListenerSaga(): SagaIterator {
    yield takeLeading(discoverProjectEnvironmentTypes, discoverProjectEnvironmentTypesSaga);
}
